"use client";

import Link from "next/link";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import type { UserIdentity } from "@supabase/supabase-js";
import FeedbackWidget from "@/components/feedback-widget";
import Footer from "@/components/footer";
import { accountSignInPath } from "@/lib/auth/redirect-target";
import { normalizeDisplayName } from "@/lib/display-name";
import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import { AccountDataProvider } from "./account-data-context";

const baseNavItems: Array<{ href: string; label: string; exact?: boolean }> = [
  { href: "/account", label: "Overview", exact: true },
  { href: "/account/keys", label: "API keys" },
  { href: "/account/usage", label: "Usage" },
  { href: "/account/billing", label: "Billing" },
  { href: "/account/settings", label: "Settings" },
  { href: "/account/mcp", label: "MCP" },
] as const;

function isAuthorizedForOps(email: string | null | undefined): boolean {
  if (!email) return false;
  const raw = process.env.NEXT_PUBLIC_OPS_DASHBOARD_ALLOWED_EMAILS || "";
  const allowed = raw
    .split(",")
    .map((entry) => entry.trim().toLowerCase())
    .filter(Boolean);
  if (!allowed.length) return false;
  return allowed.includes(email.trim().toLowerCase());
}

type UserSnapshot = {
  email: string | null;
  name: string | null;
  hasPassword: boolean;
  hasMagicLink: boolean;
  identities: UserIdentity[];
};

type AccountShellProps = {
  children: ReactNode;
};

function AccountLoading() {
  return (
    <div className="min-h-screen text-[var(--text)]">
      <main className="mx-auto w-full max-w-5xl px-6 pb-24 pt-16">
        <section className="panel p-8">
          <p className="text-sm font-medium text-[var(--muted)]">
            Account
          </p>
          <p className="mt-3 text-sm text-[var(--muted)]">Loading...</p>
        </section>
      </main>
    </div>
  );
}

function HomeIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 11.5L12 4l9 7.5" />
      <path d="M5.5 10.5V20h13V10.5" />
      <path d="M9.5 20v-5h5v5" />
    </svg>
  );
}

function SignOutIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 4h5v16h-5" />
      <path d="M9 12h10" />
      <path d="m11 8-4 4 4 4" />
      <path d="M5 4h4" />
    </svg>
  );
}

export default function AccountShell({ children }: AccountShellProps) {
  const supabase = getSupabaseBrowserClient();
  const router = useRouter();
  const pathname = usePathname() ?? "";

  const [user, setUser] = useState<UserSnapshot | null>(null);
  const [identities, setIdentities] = useState<UserIdentity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [insightsAccess, setInsightsAccess] = useState<{
    email: string;
    hasAccess: boolean;
  } | null>(null);

  const navItems = useMemo(() => {
    const hasInsightsAccess =
      Boolean(user?.email) &&
      insightsAccess?.email === user?.email &&
      insightsAccess?.hasAccess === true;
    const items = hasInsightsAccess
      ? [
          ...baseNavItems.slice(0, 3),
          { href: "/account/insights", label: "Insights" },
          ...baseNavItems.slice(3),
        ]
      : baseNavItems;
    if (!isAuthorizedForOps(user?.email)) return items;
    return [...items, { href: "/account/ops", label: "Ops" }];
  }, [insightsAccess, user?.email]);

  const refreshUser = useCallback(async () => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data?.user) return;
    const metadata = data.user.user_metadata ?? {};
    const name = normalizeDisplayName(
      (metadata.full_name as string | undefined) ??
        (metadata.name as string | undefined) ??
        (metadata.preferred_username as string | undefined) ??
        (metadata.user_name as string | undefined),
    );
    setUser((prev) => ({
      email: data.user.email ?? prev?.email ?? null,
      name,
      hasPassword: Boolean(metadata.has_password ?? prev?.hasPassword),
      hasMagicLink: Boolean(metadata.has_magic_link ?? prev?.hasMagicLink),
      identities: prev?.identities ?? [],
    }));
  }, [supabase]);

  const refreshIdentities = useCallback(async () => {
    const { data, error } = await supabase.auth.getUserIdentities();
    if (error) return;
    setIdentities(data?.identities ?? []);
  }, [supabase]);

  useEffect(() => {
    let mounted = true;
    let callbackChecked = false;
    const callbackParams = new URL(window.location.href).searchParams;
    const hasCallbackError = ["error", "error_code", "error_description"].some((key) => callbackParams.has(key));
    const hasCallback = callbackParams.has("code") || hasCallbackError;

    const applySession = async (
      session: {
        user: {
          email?: string | null;
          user_metadata?: Record<string, unknown>;
          identities?: UserIdentity[];
        };
      } | null
    ) => {
      if (!mounted || !callbackChecked) return;
      if (!session) {
        setIsLoading(false);
        router.replace(accountSignInPath(pathname, window.location.hash));
        return;
      }

      const metadata = session.user.user_metadata ?? {};
      const name = normalizeDisplayName(
        (metadata.full_name as string | undefined) ??
          (metadata.name as string | undefined) ??
          (metadata.preferred_username as string | undefined) ??
          (metadata.user_name as string | undefined),
      );
      setUser({
        email: session.user.email ?? null,
        name,
        hasPassword: Boolean(
          (metadata as { has_password?: boolean }).has_password
        ),
        hasMagicLink: Boolean(
          (metadata as { has_magic_link?: boolean }).has_magic_link
        ),
        identities: session.user.identities ?? [],
      });
      setIdentities(session.user.identities ?? []);
      setIsLoading(false);
      if ((session.user.identities ?? []).length === 0) {
        void refreshIdentities();
      }
    };

    supabase.auth
      .initialize()
      .then(async ({ error }) => {
        if (!mounted) return;
        // Failed URL authentication deliberately preserves an earlier session.
        // Never expose that account as if the incoming email link succeeded.
        if (hasCallback && (error || hasCallbackError || new URL(window.location.href).searchParams.has("code"))) {
          router.replace("/signin?error=auth_confirm_failed");
          return;
        }
        callbackChecked = true;
        const { data } = await supabase.auth.getSession();
        await applySession(data.session);
      })
      .catch(() => {
        if (!mounted) return;
        router.replace(hasCallback ? "/signin?error=auth_confirm_failed" : "/signin");
      });

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (
        event === "INITIAL_SESSION" ||
        event === "SIGNED_IN" ||
        event === "TOKEN_REFRESHED" ||
        event === "USER_UPDATED"
      ) {
        void applySession(session);
      }
      if (event === "SIGNED_OUT") {
        router.replace(accountSignInPath(pathname, window.location.hash));
      }
    });

    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, [refreshIdentities, router, supabase, pathname]);

  useEffect(() => {
    const email = user?.email;
    if (isLoading || !email) {
      return;
    }

    let mounted = true;
    fetch("/api/account/insights/access", { cache: "no-store" })
      .then((response) => response.json())
      .then((payload) => {
        if (!mounted) return;
        setInsightsAccess({ email, hasAccess: Boolean(payload?.hasAccess) });
      })
      .catch(() => {
        if (!mounted) return;
        setInsightsAccess({ email, hasAccess: false });
      });

    return () => {
      mounted = false;
    };
  }, [isLoading, user?.email]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    router.replace(accountSignInPath(pathname, window.location.hash));
  };

  const accountData = useMemo(
    () => ({
      email: user?.email ?? null,
      name: user?.name ?? null,
      identities,
      hasPassword: user?.hasPassword ?? false,
      hasMagicLink: user?.hasMagicLink ?? false,
      isLoading,
      setName: (name: string | null) =>
        setUser((prev) => ({
          email: prev?.email ?? null,
          name,
          hasPassword: prev?.hasPassword ?? false,
          hasMagicLink: prev?.hasMagicLink ?? false,
          identities: prev?.identities ?? [],
        })),
      setHasPassword: (value: boolean) =>
        setUser((prev) => ({
          email: prev?.email ?? null,
          name: prev?.name ?? null,
          hasPassword: value,
          hasMagicLink: prev?.hasMagicLink ?? false,
          identities: prev?.identities ?? [],
        })),
      setHasMagicLink: (value: boolean) =>
        setUser((prev) => ({
          email: prev?.email ?? null,
          name: prev?.name ?? null,
          hasPassword: prev?.hasPassword ?? false,
          hasMagicLink: value,
          identities: prev?.identities ?? [],
        })),
      setIdentities,
      refreshUser,
      refreshIdentities,
    }),
    [
      identities,
      isLoading,
      refreshIdentities,
      refreshUser,
      user?.email,
      user?.hasMagicLink,
      user?.hasPassword,
      user?.name,
    ]
  );

  if (isLoading) {
    return <AccountLoading />;
  }

  return (
    <AccountDataProvider value={accountData}>
      <div className="min-h-screen text-[var(--text)]">
        <div className="mx-auto w-full max-w-6xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10 lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start lg:gap-8">
          <aside className="mb-6 w-full min-w-0 shrink-0 space-y-6 lg:sticky lg:top-10 lg:mb-0">
            <section className="panel space-y-3 p-4 lg:space-y-4 lg:p-6">
              <div className="flex min-w-0 items-start justify-between gap-3 lg:block">
                <div className="min-w-0"><p className="text-sm font-medium text-[var(--muted)]">
                  Account
                </p>
                {user?.email ? (
                  <p className="mt-2 break-all text-sm text-[var(--text)]">{user.email}</p>
                ) : null}
              </div>

              <div className="grid shrink-0 grid-cols-2 gap-2 lg:mt-4 lg:gap-3">
                <Link
                  href="/"
                  aria-label="Home"
                  className="group flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 px-3 py-2 lg:px-4 lg:py-3 text-[var(--muted)] transition hover:border-white/30 hover:text-[var(--text)]"
                >
                  <span className="group-hover:hidden">
                    <HomeIcon />
                  </span>
                  <span className="hidden text-sm group-hover:inline">
                    Home
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={handleSignOut}
                  aria-label="Sign out"
                  className="group flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 px-3 py-2 lg:px-4 lg:py-3 text-[var(--muted)] transition hover:border-white/30 hover:text-[var(--text)]"
                >
                  <span className="group-hover:hidden">
                    <SignOutIcon />
                  </span>
                  <span className="hidden text-sm group-hover:inline">
                    Sign out
                  </span>
                </button>
              </div>

              </div>
              <nav aria-label="Account" className="grid grid-cols-3 gap-2 lg:grid-cols-1">
                {navItems.map((item) => {
                  const isActive = item.exact
                    ? pathname === item.href
                    : pathname.startsWith(item.href);

                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      aria-current={isActive ? "page" : undefined}
                      className={`group block whitespace-nowrap rounded-xl border px-2 py-3 lg:px-3 text-left font-[family-name:var(--font-display-family)] text-sm font-medium transition ${
                        isActive
                          ? "border-[var(--accent)] bg-white/10 text-[var(--text)]"
                          : "border-white/10 bg-white/5 text-[var(--muted)] hover:border-white/30 hover:text-[var(--text)]"
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
            </section>
          </aside>

          <main className="min-w-0">{children}</main>
        </div>

        <FeedbackWidget />
        <Footer showAuthCtas={false} showPreferences />
      </div>
    </AccountDataProvider>
  );
}
